// Old-school whimsical BRIGHT DAY forest scene — three diverse children exploring
// alongside forest creatures. Storybook-illustration feel with bold colors.
export default function HeroIllustration({ className = "", ariaLabel = "Three diverse children exploring a bright whimsical forest with animal friends" }) {
  return (
    <svg
      viewBox="0 0 800 800"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label={ariaLabel}
      data-testid="hero-illustration"
    >
      <defs>
        <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#8DC7FF" />
          <stop offset="1" stopColor="#DFF3FF" />
        </linearGradient>
        <linearGradient id="floor" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#9CE39C" />
          <stop offset="1" stopColor="#4EB264" />
        </linearGradient>
        <linearGradient id="floorDark" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#67C36E" />
          <stop offset="1" stopColor="#3B9C4B" />
        </linearGradient>
        <linearGradient id="pine" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#6BC96F" />
          <stop offset="1" stopColor="#2E8C3E" />
        </linearGradient>
        <linearGradient id="pineDark" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#4EAF52" />
          <stop offset="1" stopColor="#256B2B" />
        </linearGradient>
        <radialGradient id="canopy" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#9CE39C" />
          <stop offset="1" stopColor="#3B9C4B" />
        </radialGradient>
        <radialGradient id="sun" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#FFF6A0" />
          <stop offset="0.7" stopColor="#FFD93D" />
          <stop offset="1" stopColor="#FFB347" />
        </radialGradient>
      </defs>

      <rect x="0" y="0" width="800" height="800" rx="40" ry="40" fill="url(#sky)" />

      <g transform="translate(680 130)">
        <g opacity="0.85">
          {Array.from({ length: 12 }).map((_, i) => (
            <rect key={i} x="-3.5" y="-92" width="7" height="26" rx="3" fill="#FFD93D"
              transform={`rotate(${i * 30})`} />
          ))}
        </g>
        <circle r="54" fill="url(#sun)" />
        <circle cx="-14" cy="-4" r="4" fill="#3D4EA0" />
        <circle cx="16" cy="-4" r="4" fill="#3D4EA0" />
        <path d="M -12 12 Q 0 22 12 12" stroke="#3D4EA0" strokeWidth="3" strokeLinecap="round" fill="none" />
        <circle cx="-20" cy="10" r="6" fill="#FF9CB8" opacity="0.75" />
        <circle cx="20" cy="10" r="6" fill="#FF9CB8" opacity="0.75" />
      </g>

      <Cloud x={140} y={170} />
      <Cloud x={420} y={130} scale={0.75} />
      <Cloud x={550} y={220} scale={0.9} />

      {[
        { x: 80, y: 300, s: 6, c: "#FFD93D" },
        { x: 260, y: 260, s: 5, c: "#FFFFFF" },
        { x: 500, y: 340, s: 6, c: "#FFFFFF" },
        { x: 720, y: 320, s: 6, c: "#FFD93D" },
      ].map((star, i) => (
        <Sparkle key={i} x={star.x} y={star.y} size={star.s} color={star.c} />
      ))}

      {/* Tall pines planted along the ground curve, behind the deer.
          Round "poof" trees intentionally float higher in the air like whimsical canopies. */}
      <PineTree x={40}  y={610} h={190} colorId="pineDark" />
      <PineTree x={760} y={625} h={185} colorId="pineDark" />
      <PineTree x={180} y={575} h={230} colorId="pine" />
      <PineTree x={620} y={645} h={220} colorId="pine" />

      <RoundTree x={270} y={410} r={70} />
      <RoundTree x={540} y={420} r={68} />

      <g transform="translate(430 300)">
        <ellipse cx="-9" cy="-4" rx="10" ry="8" fill="#FF6B9D" />
        <ellipse cx="9" cy="-4" rx="10" ry="8" fill="#FF6B9D" />
        <ellipse cx="-8" cy="8" rx="7" ry="6" fill="#FFD93D" />
        <ellipse cx="8" cy="8" rx="7" ry="6" fill="#FFD93D" />
        <rect x="-1.5" y="-8" width="3" height="20" rx="1.5" fill="#3D4EA0" />
        <circle cx="0" cy="-10" r="2" fill="#3D4EA0" />
      </g>

      <path d="M 320 200 q 8 -8 14 0 q 8 -8 14 0" stroke="#3D4EA0" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M 600 90 q 6 -6 12 0 q 6 -6 12 0" stroke="#3D4EA0" strokeWidth="2.5" fill="none" strokeLinecap="round" />

      <path d="M -20 620 Q 200 550 400 620 T 820 620 L 820 820 L -20 820 Z" fill="url(#floor)" />
      <path d="M -20 680 Q 240 620 500 680 T 820 680 L 820 820 L -20 820 Z" fill="url(#floorDark)" />

      <Mushroom x={90} y={720} cap="#FF6B9D" />
      <Mushroom x={130} y={730} cap="#FF6B9D" small />
      <Mushroom x={660} y={730} cap="#FFD93D" />
      <Mushroom x={730} y={720} cap="#FF8C42" small />
      <Mushroom x={370} y={740} cap="#B49FFF" small />

      {[60, 200, 340, 480, 610, 730].map((x, i) => (
        <path key={i} d={`M ${x} 770 q -3 -10 0 -18 q 3 10 6 18 q 3 -10 6 -18 q 3 8 0 18 Z`} fill="#256B2B" opacity="0.7" />
      ))}
      {[
        { x: 240, y: 730, c: "#FF6B9D" },
        { x: 620, y: 730, c: "#B49FFF" },
        { x: 55, y: 745, c: "#FFD93D" },
      ].map((f, i) => (
        <Flower key={i} x={f.x} y={f.y} color={f.c} />
      ))}

      <g transform="translate(150 380)">
        <ellipse cx="0" cy="8" rx="20" ry="24" fill="#8A6B4A" />
        <ellipse cx="0" cy="12" rx="14" ry="18" fill="#E7C89A" />
        <path d="M -20 4 Q -28 20 -18 30 Z" fill="#5C4630" />
        <path d="M 20 4 Q 28 20 18 30 Z" fill="#5C4630" />
        <circle cx="-7" cy="-2" r="7" fill="#FFFFFF" />
        <circle cx="7" cy="-2" r="7" fill="#FFFFFF" />
        <circle cx="-7" cy="-2" r="4" fill="#1F2140" />
        <circle cx="7" cy="-2" r="4" fill="#1F2140" />
        <circle cx="-6" cy="-3" r="1.4" fill="#FFFFFF" />
        <circle cx="8" cy="-3" r="1.4" fill="#FFFFFF" />
        <path d="M -3 5 L 3 5 L 0 10 Z" fill="#FF8C42" />
        <path d="M -14 -16 L -10 -8 L -18 -6 Z" fill="#8A6B4A" />
        <path d="M 14 -16 L 10 -8 L 18 -6 Z" fill="#8A6B4A" />
      </g>

      <g transform="translate(680 665)">
        <path d="M 30 -10 Q 60 -18 70 4 Q 60 8 40 6 Z" fill="#FF8C42" />
        <path d="M 62 -8 Q 72 -6 70 4 L 60 3 Z" fill="#FFF6D6" />
        <ellipse cx="10" cy="6" rx="30" ry="20" fill="#FF8C42" />
        <ellipse cx="-8" cy="14" rx="10" ry="10" fill="#FFF6D6" />
        <rect x="-6" y="20" width="6" height="12" rx="2" fill="#8A4A20" />
        <rect x="18" y="22" width="6" height="10" rx="2" fill="#8A4A20" />
        <path d="M -30 0 Q -34 -12 -22 -16 L -8 -14 Q -6 -6 -14 -2 Z" fill="#FF8C42" />
        <path d="M -28 -12 L -22 -22 L -20 -14 Z" fill="#FF8C42" />
        <path d="M -20 -14 L -14 -22 L -12 -12 Z" fill="#FF8C42" />
        <path d="M -26 -13 L -22 -18 L -21 -14 Z" fill="#1F2140" />
        <path d="M -30 0 L -36 -4 L -30 -6 Z" fill="#FFF6D6" />
        <circle cx="-35" cy="-2" r="1.6" fill="#1F2140" />
        <circle cx="-22" cy="-8" r="1.6" fill="#1F2140" />
      </g>

      <g transform="translate(90 700)">
        <ellipse cx="0" cy="10" rx="16" ry="14" fill="#F2E8D8" />
        <ellipse cx="0" cy="14" rx="9" ry="8" fill="#FFFFFF" />
        <circle cx="0" cy="-6" r="12" fill="#F2E8D8" />
        <ellipse cx="-5" cy="-22" rx="4" ry="12" fill="#F2E8D8" />
        <ellipse cx="5" cy="-22" rx="4" ry="12" fill="#F2E8D8" />
        <ellipse cx="-5" cy="-22" rx="2" ry="9" fill="#FFB6D4" />
        <ellipse cx="5" cy="-22" rx="2" ry="9" fill="#FFB6D4" />
        <circle cx="-3.5" cy="-6" r="1.4" fill="#1F2140" />
        <circle cx="3.5" cy="-6" r="1.4" fill="#1F2140" />
        <path d="M -1.5 -2 L 1.5 -2 L 0 0 Z" fill="#FF6B9D" />
        <circle cx="-7" cy="-2" r="2" fill="#FF6B9D" opacity="0.4" />
        <circle cx="7" cy="-2" r="2" fill="#FF6B9D" opacity="0.4" />
        <ellipse cx="-8" cy="22" rx="5" ry="3" fill="#FFFFFF" />
        <ellipse cx="8" cy="22" rx="5" ry="3" fill="#FFFFFF" />
      </g>

      <g transform="translate(580 730)">
        <path d="M -22 4 Q -18 -14 0 -18 Q 18 -14 22 4 Z" fill="#5C4630" />
        {Array.from({ length: 9 }).map((_, i) => {
          const a = -70 + i * 17;
          const x = Math.cos((a * Math.PI) / 180) * 20;
          const y = Math.sin((a * Math.PI) / 180) * 20;
          return <line key={i} x1={x * 0.6} y1={y * 0.6} x2={x} y2={y} stroke="#1F2140" strokeWidth="1.5" />;
        })}
        <ellipse cx="-4" cy="4" rx="16" ry="10" fill="#E7C89A" />
        <circle cx="-14" cy="0" r="1.6" fill="#1F2140" />
        <circle cx="-10" cy="6" r="1.6" fill="#1F2140" />
        <circle cx="-19" cy="6" r="2" fill="#1F2140" />
      </g>

      {/* Mama deer + two curious fawns in the clearing */}
      <Deer x={415} y={620} scale={1}    flip={false} />
      <Deer x={335} y={643} scale={0.55} flip={false} spots />
      <Deer x={510} y={643} scale={0.55} flip={true}  spots />
    </svg>
  );
}

function Cloud({ x, y, scale = 1 }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <ellipse cx="0" cy="10" rx="52" ry="20" fill="#FFFFFF" />
      <circle cx="-30" cy="-2" r="22" fill="#FFFFFF" />
      <circle cx="10" cy="-14" r="26" fill="#FFFFFF" />
      <circle cx="38" cy="0" r="18" fill="#FFFFFF" />
    </g>
  );
}

function Sparkle({ x, y, size, color }) {
  const s = size;
  return (
    <g transform={`translate(${x} ${y})`}>
      <path
        d={`M 0 ${-s} L ${s * 0.3} ${-s * 0.3} L ${s} 0 L ${s * 0.3} ${s * 0.3} L 0 ${s} L ${-s * 0.3} ${s * 0.3} L ${-s} 0 L ${-s * 0.3} ${-s * 0.3} Z`}
        fill={color}
      />
    </g>
  );
}

function Flower({ x, y, color }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <line x1="0" y1="0" x2="0" y2="20" stroke="#2E8C67" strokeWidth="3" strokeLinecap="round" />
      <ellipse cx="-6" cy="14" rx="6" ry="3" fill="#5CBB93" />
      {[0, 72, 144, 216, 288].map((angle) => (
        <circle key={angle} r="5" fill={color} transform={`rotate(${angle}) translate(0 -7)`} />
      ))}
      <circle r="3.5" fill="#FFD93D" />
    </g>
  );
}

function Mushroom({ x, y, cap, small = false }) {
  const s = small ? 0.7 : 1;
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <path d="M -8 0 Q -6 20 0 24 Q 6 20 8 0 Z" fill="#FFFFFF" />
      <path d="M -22 0 Q -20 -22 0 -22 Q 20 -22 22 0 Z" fill={cap} />
      <circle cx="-8" cy="-10" r="3" fill="#FFFFFF" />
      <circle cx="6" cy="-14" r="4" fill="#FFFFFF" />
      <circle cx="12" cy="-4" r="3" fill="#FFFFFF" />
    </g>
  );
}

function PineTree({ x, y, h, colorId }) {
  const w = h * 0.55;
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect x={-6} y={-10} width={12} height={30} fill="#5C3A22" />
      <path d={`M ${-w / 2} 0 L 0 ${-h * 0.55} L ${w / 2} 0 Z`} fill={`url(#${colorId})`} />
      <path d={`M ${-w / 2.3} ${-h * 0.32} L 0 ${-h * 0.82} L ${w / 2.3} ${-h * 0.32} Z`} fill={`url(#${colorId})`} />
      <path d={`M ${-w / 2.8} ${-h * 0.6} L 0 ${-h} L ${w / 2.8} ${-h * 0.6} Z`} fill={`url(#${colorId})`} />
    </g>
  );
}

function RoundTree({ x, y, r }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect x={-8} y={0} width={16} height={r * 0.9} fill="#5C3A22" />
      <circle r={r} fill="url(#canopy)" />
      <circle cx={-r * 0.35} cy={-r * 0.15} r={r * 0.4} fill="#9CE39C" opacity="0.9" />
      <circle cx={r * 0.35} cy={-r * 0.1} r={r * 0.38} fill="#9CE39C" opacity="0.9" />
      <circle cx={-r * 0.2} cy={r * 0.1} r="3" fill="#FF6B9D" />
      <circle cx={r * 0.15} cy={-r * 0.35} r="3" fill="#FFD93D" />
      <circle cx={r * 0.4} cy={r * 0.15} r="3" fill="#FF6B9D" />
    </g>
  );
}


function Deer({ x, y, scale = 1, flip = false, spots = false }) {
  const INK = "#1F2140";
  // Cute standing deer facing right by default (flip=true mirrors it).
  // Reference (x, y) is the top-of-legs / shoulder level. Feet reach ~y + 46*scale.
  const dir = flip ? -1 : 1;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale * dir} ${scale})`}>
      {/* Legs */}
      <rect x="-30" y="0"  width="7" height="46" rx="2.5" fill="#B47A3B" />
      <rect x="-14" y="0"  width="7" height="46" rx="2.5" fill="#B47A3B" />
      <rect x="12"  y="0"  width="7" height="46" rx="2.5" fill="#B47A3B" />
      <rect x="28"  y="0"  width="7" height="46" rx="2.5" fill="#B47A3B" />
      {/* Hooves */}
      <ellipse cx="-26.5" cy="47" rx="5.5" ry="3" fill="#3A2515" />
      <ellipse cx="-10.5" cy="47" rx="5.5" ry="3" fill="#3A2515" />
      <ellipse cx="15.5"  cy="47" rx="5.5" ry="3" fill="#3A2515" />
      <ellipse cx="31.5"  cy="47" rx="5.5" ry="3" fill="#3A2515" />

      {/* Body */}
      <ellipse cx="0" cy="-8" rx="40" ry="20" fill="#B47A3B" />
      {/* Belly */}
      <ellipse cx="2" cy="4" rx="28" ry="8" fill="#FFF6D6" />

      {/* Spots for fawn */}
      {spots && (
        <g fill="#FFF6D6" opacity="0.95">
          <ellipse cx="-22" cy="-16" rx="3" ry="2.2" />
          <ellipse cx="-10" cy="-20" rx="3" ry="2.2" />
          <ellipse cx="2"   cy="-16" rx="3" ry="2.2" />
          <ellipse cx="14"  cy="-20" rx="3" ry="2.2" />
          <ellipse cx="-16" cy="-6"  rx="2.6" ry="2" />
          <ellipse cx="6"   cy="-6"  rx="2.6" ry="2" />
          <ellipse cx="-4"  cy="-24" rx="2.6" ry="2" />
        </g>
      )}

      {/* Tail */}
      <g transform="translate(-38 -16) rotate(-30)">
        <ellipse cx="0" cy="0" rx="6" ry="4" fill="#B47A3B" />
        <ellipse cx="-2" cy="-1" rx="3" ry="2.4" fill="#FFF6D6" />
      </g>

      {/* Neck */}
      <path d="M 28 -22 Q 44 -34 55 -28 L 52 -6 Q 40 0 28 -6 Z" fill="#B47A3B" />

      {/* Head */}
      <ellipse cx="58" cy="-32" rx="14" ry="12" fill="#B47A3B" />
      {/* Snout */}
      <ellipse cx="68" cy="-26" rx="8" ry="6" fill="#B47A3B" />
      {/* Snout light patch */}
      <ellipse cx="66" cy="-24" rx="6" ry="4" fill="#FFF6D6" />
      {/* Nose */}
      <ellipse cx="72" cy="-26" rx="3" ry="2" fill="#3A2515" />
      <circle cx="72.5" cy="-27" r="0.8" fill="#FFFFFF" opacity="0.7" />
      {/* Mouth */}
      <path d="M 70 -22 Q 66 -20 62 -22" stroke="#3A2515" strokeWidth="1.4" fill="none" strokeLinecap="round" />

      {/* Ears */}
      <g transform="translate(52 -46) rotate(-22)">
        <ellipse cx="0" cy="0" rx="4" ry="10" fill="#B47A3B" />
        <ellipse cx="-0.5" cy="1" rx="1.8" ry="6.5" fill="#FF9CB8" />
      </g>
      <g transform="translate(64 -46) rotate(18)">
        <ellipse cx="0" cy="0" rx="4" ry="10" fill="#B47A3B" />
        <ellipse cx="0.5" cy="1" rx="1.8" ry="6.5" fill="#FF9CB8" />
      </g>

      {/* Big gentle eye */}
      <ellipse cx="60" cy="-34" rx="3.2" ry="4.2" fill="#FFFFFF" />
      <ellipse cx="60" cy="-33" rx="2.4" ry="3.2" fill="#3A2515" />
      <circle cx="60" cy="-32.5" r="1.3" fill={INK} />
      <circle cx="60.6" cy="-34" r="0.7" fill="#FFFFFF" />
      {/* Lashes */}
      <line x1="57" y1="-37" x2="55" y2="-38.5" stroke={INK} strokeWidth="1.3" strokeLinecap="round" />
      <line x1="60" y1="-38" x2="60" y2="-40" stroke={INK} strokeWidth="1.3" strokeLinecap="round" />
      <line x1="63" y1="-37" x2="65" y2="-38.5" stroke={INK} strokeWidth="1.3" strokeLinecap="round" />

      {/* Rosy cheek */}
      <ellipse cx="65" cy="-28" rx="2.6" ry="1.8" fill="#FF6B9D" opacity="0.55" />

      {/* Small antler nub for adult only (subtle, doe-friendly) — omitted for now to keep it a doe */}
    </g>
  );
}
